import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dynamic from 'next/dynamic';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  FormControlLabel,
  Grid,
  List,
  Paper,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Hotel as HotelIcon,
  PostAdd as PostAddIcon,
  School as SchoolIcon,
} from '@mui/icons-material';

import TabPanel from 'components/Shared/TabPanel';

import SelectComponent from 'components/SelectComponent';
import DatepickerComponent from 'components/DatepickerComponent';
import AccommodationsComponent from 'components/SchoolsComponents/BuildPackageComponent/AccommodationsComponent';
import AdditionalListItemComponent from 'components/SchoolsComponents/BuildPackageComponent/AdditionalListItemComponent';
import CourseListItemComponent from 'components/SchoolsComponents/BuildPackageComponent/CourseListItemComponent';

import {
  UpdateAccommodation,
  UpdateAdditionalService,
  UpdateCourse,
} from 'store/actions/schoolDetailActions';
import { UnsetAccommodationAdditional } from 'utils/additionalServicesUtils';

import {
  getSelectedCourses,
  selectCheapestCourseByWeeks,
  verifyPriceRangesByWeeks,
} from 'utils/courseUtils';
import {
  formatDate,
  getDaysAdded,
  getDaysSubtracted,
  getDifferenceInDays,
  getGroupedMondayOptions,
  getNextWeekDay,
  getUtcDate,
  getWeekRange,
  getWeeksAdded,
} from 'utils/date';

export const SelectGroupComponent = Dynamic(
  () => import('components/SelectGroupComponent'),
  { ssr: false }
);

function BuildPackageComponent({ dateHandler }) {
  const FRIDAY = 5;
  const SATURDAY = 6;
  const theme = useTheme();
  const deskBreakpoint = useMediaQuery(theme.breakpoints.up('sm'));
  const dispatch = useDispatch();
  const detailReducer = useSelector((store) => store.schoolDetailReducer);
  const { accommodationGroups, additionalServices } = detailReducer;
  const [needAccommodation, setNeedAccommodation] = useState(
    detailReducer.needAccommodation
  );
  const { dateParams, setDateParams } = dateHandler;
  const [value, setValue] = useState(0);

  const getCourseEnd = (newCourseStart, weeks) =>
    getNextWeekDay(getWeeksAdded(newCourseStart, weeks - 1), FRIDAY);

  const accomodationFixedDate = getDaysAdded(
    getCourseEnd(dateParams.courseStart, dateParams.courseWeeks),
    1
  );

  const [accommodationEndDate, setAccommodationEndDate] = useState(
    getDaysAdded(
      getCourseEnd(dateParams.courseStart, dateParams.courseWeeks),
      1
    )
  );
  const [accommodationWeeks, setAccommodationWeeks] = useState(
    dateParams.courseWeeks
  );
  const textColorPrimary = { color: '#2A1D1D' };
  const needAccommodationText = needAccommodation
    ? 'Sim, quero uma acomodação'
    : 'Não precisarei de acomodação';

  useEffect(() => {
    setAccommodationWeeks(dateParams.courseWeeks);
    setAccommodationEndDate(
      getDaysAdded(
        getCourseEnd(dateParams.courseStart, dateParams.courseWeeks),
        1
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams.courseWeeks, dateParams.courseStart]);

  useEffect(() => {
    dateParams.accommodationEnd = accommodationEndDate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabs = (event, activeTab) => setValue(activeTab);
  const showAccommodationTab = () => accommodationGroups.length > 0;

  const setCourseByWeeks = (weeks) => {
    const selectedCourses = getSelectedCourses(detailReducer.courses);

    if (selectedCourses[0] && selectedCourses[0].program.id === 2) {
      const valid = verifyPriceRangesByWeeks(selectedCourses, weeks);

      if (!valid)
        UpdateCourse(
          dispatch,
          selectCheapestCourseByWeeks(detailReducer.courses, weeks)
        );
    }
  };

  const activateAccommodation = (activate = true) => {
    detailReducer.needAccommodation = activate;
    setNeedAccommodation(activate);

    UpdateAccommodation(dispatch, accommodationGroups);
  };

  const updateAccommodationDates = (selectedWeeks) => {
    const { courseStart } = dateParams;
    const startDate = getUtcDate(courseStart);
    const accommodationStart = getDaysSubtracted(startDate, 1);
    const accommodationEnd = getNextWeekDay(
      getWeeksAdded(startDate, selectedWeeks - 1),
      SATURDAY
    );

    setAccommodationEndDate(accommodationEnd);
    setDateParams({
      ...dateParams,
      accommodationStart,
      accommodationEnd,
    });
  };

  return (
    <>
      <Box
        bgcolor="secondary.main"
        borderRadius="4px 4px 0 0"
        color="primary.contrastText"
        id="buildPackage"
        py={1}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 900,
            margin: '8px',
            textAlign: 'center',
            textTransform: 'uppercase',
          }}
        >
          Monte o seu intercâmbio
        </Typography>
      </Box>

      <Box
        bgcolor="background.paper"
        border={1}
        borderColor="border.main"
        borderRadius="0 0 4px 4px"
        position="relative"
      >
        <Box>
          <Paper square>
            <Tabs
              aria-label="icon label tabs intercambio cursos acomodação adicionais"
              indicatorColor="primary"
              onChange={handleTabs}
              textColor="primary"
              value={value}
              variant="fullWidth"
            >
              <Tab
                classes={{ textColorPrimary }}
                icon={<SchoolIcon />}
                id="courseTab"
                label={deskBreakpoint ? '1 - Curso' : 'Passo 1'}
              />

              <Tab
                classes={{ textColorPrimary }}
                icon={<HotelIcon />}
                id="accommodationTab"
                label={deskBreakpoint ? '2 - Acomodação' : 'Passo 2'}
                onClick={activateAccommodation}
                style={{ display: showAccommodationTab() ? 'flex' : 'none' }}
              />

              <Tab
                classes={{ textColorPrimary }}
                icon={<PostAddIcon />}
                id="additionalTab"
                label={deskBreakpoint ? '3 - Serviços Adicionais' : 'Passo 3'}
              />
            </Tabs>
          </Paper>

          <TabPanel value={value} index={0}>
            <Box m={2}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={12}>
                  <Typography
                    component="h3"
                    sx={{ marginBottom: '8px' }}
                    variant="h5"
                  >
                    Escolha seu Curso
                  </Typography>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <SelectGroupComponent
                    disableMargin
                    id="boardDate"
                    name="date"
                    text="Data de início"
                    groups={getGroupedMondayOptions(
                      new Date(detailReducer?.fareDateValidUntil)
                    )}
                    change={(selectedDate) => {
                      const courseStart = new Date(
                        selectedDate?.replaceAll('-', '/')
                      );

                      const courseEnd = getCourseEnd(
                        courseStart,
                        dateParams?.courseWeeks
                      );
                      const accommodationStart = getDaysAdded(courseStart, -1);
                      const accommodationEnd = getDaysAdded(courseEnd, 1);

                      setDateParams({
                        ...dateParams,
                        accommodationEnd,
                        accommodationStart,
                        courseEnd,
                        courseStart,
                      });
                    }}
                    value={formatDate(dateParams?.courseStart, 'iso-string')}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <SelectComponent
                    disableMargin
                    id="courseWeekQuantity"
                    isDuplicateText={false}
                    items={getWeekRange(detailReducer.weeks)}
                    name="courseWeekQuantity"
                    text="Semanas"
                    value={parseInt(dateParams.courseWeeks, 10)}
                    change={(selectedWeeks) => {
                      const courseEnd = getCourseEnd(
                        new Date(dateParams.courseStart),
                        selectedWeeks
                      );
                      const accommodationEnd = getDaysAdded(courseEnd, 1);

                      setCourseByWeeks(selectedWeeks);
                      setDateParams({
                        ...dateParams,
                        accommodationEnd,
                        courseEnd,
                        courseWeeks: parseInt(selectedWeeks, 10),
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12}>
                  <CourseListItemComponent
                    dateHandler={{ dateParams, setDateParams }}
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {showAccommodationTab() && (
            <TabPanel value={value} index={1}>
              <Box m={2}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12}>
                    <Typography component="h3" variant="h5">
                      Garanta sua Acomodação
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Box mb={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!needAccommodation}
                            onChange={() => {
                              activateAccommodation(!needAccommodation);

                              if (needAccommodation)
                                UpdateAdditionalService(
                                  dispatch,
                                  UnsetAccommodationAdditional(
                                    additionalServices
                                  )
                                );
                            }}
                          />
                        }
                        label={needAccommodationText}
                      />
                    </Box>
                  </Grid>

                  {needAccommodation && (
                    <>
                      <Grid item xs={3} sm={4}>
                        <SelectComponent
                          disableMargin
                          id="accommodationWeekQuantity"
                          isDuplicateText={false}
                          items={getWeekRange(
                            detailReducer.accommodationWeeks.filter(
                              (w) => w <= dateParams.courseWeeks
                            ),
                            deskBreakpoint
                          )}
                          name="accommodationWeekQuantity"
                          text="Semanas"
                          value={parseInt(accommodationWeeks, 10)}
                          change={(selectedWeeks) => {
                            setAccommodationWeeks(selectedWeeks);
                            updateAccommodationDates(selectedWeeks);
                          }}
                        />
                      </Grid>

                      <Grid item xs={4} sm={4}>
                        <DatepickerComponent
                          fullWidth
                          allowedWeekDays={[0, 1, 2, 3, 4, 5, 6]}
                          disabled={!needAccommodation}
                          id="accommodationStartDate"
                          label="Data de início"
                          maxDate={getDaysAdded(
                            getUtcDate(dateParams.courseStart),
                            1
                          )}
                          minDate={getDaysSubtracted(
                            getUtcDate(dateParams.courseStart),
                            3
                          )}
                          value={dateParams.accommodationStart}
                          variant="outlined"
                          change={(accommodationStart) => {
                            setDateParams({
                              ...dateParams,
                              accommodationStart:
                                getUtcDate(accommodationStart),
                            });

                            setAccommodationWeeks(
                              Math.abs(
                                getDifferenceInDays(
                                  getUtcDate(accommodationStart),
                                  getUtcDate(dateParams.accommodationEnd)
                                ) / 7
                              )
                            );
                          }}
                        />
                      </Grid>

                      <Grid item xs={5} sm={4}>
                        <DatepickerComponent
                          allowedWeekDays={[0, 1, 2, 3, 4, 5, 6]}
                          disabled={!needAccommodation}
                          fullWidth
                          id="accommodationEndDate"
                          label="Data de término"
                          maxDate={getDaysAdded(
                            getUtcDate(accomodationFixedDate),
                            2
                          )}
                          minDate={getDaysSubtracted(
                            getUtcDate(accomodationFixedDate),
                            2
                          )}
                          value={dateParams.accommodationEnd}
                          variant="outlined"
                          change={(accommodationEnd) => {
                            setDateParams({
                              ...dateParams,
                              accommodationEnd: getUtcDate(accommodationEnd),
                            });

                            setAccommodationEndDate(
                              dateParams.accommodationEnd
                            );

                            setAccommodationWeeks(
                              Math.abs(
                                getDifferenceInDays(
                                  getUtcDate(dateParams.accommodationStart),
                                  getUtcDate(accommodationEnd)
                                ) / 7
                              )
                            );
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <AccommodationsComponent />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </TabPanel>
          )}

          <TabPanel value={value} index={2}>
            <Box m={2}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h5" component="h3">
                    Dê um upgrade no seu pacote
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <List>
                    <AdditionalListItemComponent dateParams={dateParams} />
                  </List>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </Box>
      </Box>
    </>
  );
}

BuildPackageComponent.propTypes = {
  dateHandler: PropTypes.instanceOf(Object).isRequired,
};

export default BuildPackageComponent;
